import { useEffect, useRef, useState, useCallback } from "react";
import { Rnd } from "react-rnd";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Save, Image as ImageIcon, Loader2, RotateCcw, AlertTriangle, Grid3X3, Eye, RefreshCw, ChevronDown, ChevronRight, Building2, User, Landmark, FileText, Receipt, LayoutTemplate, Type, Plus, Minus, Sparkles, Calculator, AlignCenter, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    fetchInvoiceTemplate,
    updateInvoiceTemplate,
    uploadInvoiceTemplateBackground,
    deleteInvoiceTemplateBackground,
    fetchInvoiceTemplateBackgroundBlob,
    fetchInvoiceTemplatePreviewPdfBlob,
    fetchInvoiceTemplatePresets,
    applyInvoiceTemplatePreset,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";

const A4_W = 595;
const A4_H = 842;

const FIELD_GROUPS = [
    { id: "invoice", label: "Faktura-data", icon: Receipt, fields: {
        invoice_number: "Fakturanummer", invoice_date: "Fakturadato", due_date: "Forfaldsdato",
        job_id: "Job-ID", job_description: "Jobbeskrivelse", note: "Note", status_badge: "Status-badge",
    } },
    { id: "company", label: "Firma (granular)", icon: Building2, fields: {
        company_name: "Firmanavn", company_legal_name: "Juridisk navn", company_address: "Firma adresse",
        company_zip: "Firma postnr.", company_city: "Firma by", company_country: "Firma land",
        company_cvr: "Firma CVR", company_vat_number: "Firma moms-nr.", company_email: "Firma email",
        company_phone: "Firma telefon", company_website: "Firma hjemmeside",
    } },
    { id: "customer", label: "Kunde (granular)", icon: User, fields: {
        customer_name: "Kunde navn", customer_company: "Kunde firma", customer_address: "Kunde adresse",
        customer_zip: "Kunde postnr.", customer_city: "Kunde by", customer_country: "Kunde land",
        customer_cvr: "Kunde CVR", customer_email: "Kunde email", customer_phone: "Kunde telefon",
    } },
    { id: "lines", label: "Linjer / tabeller", icon: Table2, fields: {
        line_items: "Linjer (timer + materialer)",
    } },
    { id: "totals", label: "Totaler", icon: Calculator, fields: {
        subtotal: "Subtotal", vat_total: "Moms", total: "Total",
    } },
    { id: "bank", label: "Bank & betaling (granular)", icon: Landmark, fields: {
        bank_name: "Bank navn", bank_reg_no: "Reg.nr.", bank_account_no: "Kontonr.",
        iban: "IBAN", swift: "SWIFT / BIC", mobilepay: "MobilePay",
        payment_reference: "Betalings-reference", payment_due_text: "Betalingsfrist-tekst",
    } },
    { id: "blocks", label: "Samlede blokke / fallback", icon: FileText, fields: {
        company_block: "Firma-blok (samlet)", customer_block: "Kunde-blok (samlet)", payment_info: "Betalingsinfo (samlet)",
    } },
];

const FIELD_LABELS = FIELD_GROUPS.reduce((acc, g) => ({ ...acc, ...g.fields }), {});
const ALL_FIELD_IDS = Object.keys(FIELD_LABELS);
const ALIGN_OPTIONS = [{ id: "left", label: "←" }, { id: "center", label: "↔" }, { id: "right", label: "→" }];
const PRESET_ICONS = { FileText, Minus, Sparkles, Calculator, AlignCenter };

const DEFAULT_FIELD = (o = {}) => ({ enabled: false, x_pct: 5, y_pct: 5, w_pct: 30, h_pct: 5, font_size: 10, font_weight: "normal", align: "left", color: "#0f172a", prefix: "", z_index: 1, ...o });
// Custom text default: footer-style, centred at the bottom — never overlaps line_items
const DEFAULT_CUSTOM = (o = {}) => ({ id: `txt_${Math.random().toString(36).slice(2, 12)}`, text: "", enabled: true, x_pct: 10, y_pct: 88, w_pct: 80, h_pct: 4, font_size: 10, font_weight: "normal", align: "center", color: "#0f172a", z_index: 2, ...o });

const snap = (v, on) => (on ? Math.round(v / 5) * 5 : v);

export default function FakturaTemplateEditor() {
    const navigate = useNavigate();
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const replaceInputRef = useRef(null);
    const objectUrlRef = useRef(null);
    const [template, setTemplate] = useState(null);
    const [customTexts, setCustomTexts] = useState([]);
    const [hasBackground, setHasBackground] = useState(false);
    const [bgUrl, setBgUrl] = useState("");
    // selectedItem: { kind: 'field' | 'custom', id: string }
    const [selectedItem, setSelectedItem] = useState({ kind: "field", id: "invoice_number" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [applyingPreset, setApplyingPreset] = useState(false);
    const [error, setError] = useState("");
    const [scale, setScale] = useState(1);
    const [snapEnabled, setSnapEnabled] = useState(true);
    const [openGroups, setOpenGroups] = useState({ custom: true, invoice: true, company: false, customer: false, lines: false, totals: false, bank: false, blocks: false });
    const [presets, setPresets] = useState([]);
    const [presetMenuOpen, setPresetMenuOpen] = useState(false);

    const revokeOldUrl = () => {
        if (objectUrlRef.current) { try { URL.revokeObjectURL(objectUrlRef.current); } catch (_e) { /* ignore */ } objectUrlRef.current = null; }
    };

    const loadBackgroundBlob = useCallback(async () => {
        revokeOldUrl();
        try {
            const blob = await fetchInvoiceTemplateBackgroundBlob();
            const url = URL.createObjectURL(blob);
            objectUrlRef.current = url;
            setBgUrl(url);
        } catch (e) {
            if (e?.response?.status !== 404) console.warn("Background fetch failed:", e);
            setBgUrl("");
        }
    }, []);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const t = await fetchInvoiceTemplate();
            setTemplate(t);
            setCustomTexts(t.custom_text_fields || []);
            const hasBg = !!t.background_url;
            setHasBackground(hasBg);
            if (hasBg) await loadBackgroundBlob(); else { revokeOldUrl(); setBgUrl(""); }
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [loadBackgroundBlob]);

    useEffect(() => { load(); return () => revokeOldUrl(); }, [load]);
    useEffect(() => { fetchInvoiceTemplatePresets().then((r) => setPresets(r.presets || [])).catch(() => { /* non-fatal */ }); }, []);

    useEffect(() => {
        const updateScale = () => {
            if (!canvasRef.current) return;
            const maxW = canvasRef.current.parentElement.clientWidth - 4;
            setScale(Math.min(1, maxW / A4_W));
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, [loading, hasBackground]);

    const fields = template?.fields || {};

    const updateField = (id, partial) => {
        setTemplate((t) => ({ ...t, fields: { ...t.fields, [id]: { ...DEFAULT_FIELD(), ...(t.fields[id] || {}), ...partial } } }));
    };
    const updateCustomText = (id, partial) => {
        setCustomTexts((arr) => arr.map((c) => c.id === id ? { ...c, ...partial } : c));
    };
    const handleAddCustomText = () => {
        const fresh = DEFAULT_CUSTOM();
        setCustomTexts((arr) => [...arr, fresh]);
        setSelectedItem({ kind: "custom", id: fresh.id });
        setOpenGroups((s) => ({ ...s, custom: true }));
    };
    const handleDeleteCustomText = (id) => {
        if (!window.confirm("Slet dette tekstfelt?")) return;
        setCustomTexts((arr) => arr.filter((c) => c.id !== id));
        if (selectedItem.kind === "custom" && selectedItem.id === id) setSelectedItem({ kind: "field", id: "invoice_number" });
    };

    const handleUpload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error("Fil for stor — max 5 MB"); return; }
        try { setUploading(true); await uploadInvoiceTemplateBackground(file); toast.success("Baggrund uploaded"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };
    const handleRemoveBackground = async () => {
        if (!window.confirm("Slet template-baggrund?")) return;
        try { await deleteInvoiceTemplateBackground(); toast.success("Baggrund fjernet"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const buildSavePayload = () => {
        const completeFields = {};
        ALL_FIELD_IDS.forEach((fid) => { completeFields[fid] = { ...DEFAULT_FIELD(), ...(template?.fields?.[fid] || {}) }; });
        return { enabled: !!template?.enabled, fields: completeFields, custom_text_fields: customTexts };
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const res = await updateInvoiceTemplate(buildSavePayload());
            toast.success(`Template gemt — ${res?.field_count || ALL_FIELD_IDS.length} felter + ${res?.custom_text_count ?? customTexts.length} egne tekster`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const handleToggleEnabled = async () => {
        const newEnabled = !template.enabled;
        try {
            const payload = buildSavePayload(); payload.enabled = newEnabled;
            await updateInvoiceTemplate(payload);
            setTemplate((t) => ({ ...t, enabled: newEnabled }));
            toast.success(newEnabled ? "Template aktiveret" : "Standard layout aktiveret");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const handleApplyPreset = async (presetId) => {
        const preset = presets.find((p) => p.id === presetId);
        if (!preset) return;
        if (!window.confirm(`Anvend skabelonen "${preset.label}"? Dette overskriver alle nuværende felt-positioner.`)) return;
        try {
            setApplyingPreset(true);
            setPresetMenuOpen(false);
            const res = await applyInvoiceTemplatePreset(presetId);
            toast.success(`"${res.preset_label}" anvendt — ${res.enabled_field_count} felter aktiveret`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setApplyingPreset(false); }
    };

    const handleResetField = (id) => {
        if (!window.confirm(`Nulstil position for "${FIELD_LABELS[id]}"?`)) return;
        load();
    };

    const handlePreviewPdf = async () => {
        try {
            setPreviewing(true);
            await updateInvoiceTemplate(buildSavePayload());
            const blob = await fetchInvoiceTemplatePreviewPdfBlob();
            const url = URL.createObjectURL(blob);
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!w) {
                const a = document.createElement("a"); a.href = url; a.download = "faktura-preview.pdf";
                document.body.appendChild(a); a.click(); a.remove();
            }
            setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_e) { /* ignore */ } }, 30_000);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPreviewing(false); }
    };

    if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
    if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div></div>;

    // Compute current selection meta + setter
    let sel, setSel, selLabel, isCustomSel = false;
    if (selectedItem.kind === "custom") {
        sel = customTexts.find((c) => c.id === selectedItem.id) || DEFAULT_CUSTOM();
        setSel = (partial) => updateCustomText(selectedItem.id, partial);
        selLabel = `Egen tekst — "${(sel.text || "").slice(0, 24)}${(sel.text || "").length > 24 ? "…" : ""}"`;
        isCustomSel = true;
    } else {
        sel = fields[selectedItem.id] || DEFAULT_FIELD();
        setSel = (partial) => updateField(selectedItem.id, partial);
        selLabel = FIELD_LABELS[selectedItem.id] || "—";
    }

    const enabledCount = ALL_FIELD_IDS.reduce((n, fid) => n + (fields[fid]?.enabled ? 1 : 0), 0) + customTexts.filter((c) => c.enabled && c.text).length;

    return (
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="template-editor-page">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <button onClick={() => navigate("/dashboard/faktura-klar")} className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 text-sm" data-testid="template-back">
                    <ArrowLeft className="w-4 h-4" />Tilbage
                </button>
                <div className="flex-1 min-w-[200px]">
                    <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2"><ImageIcon className="w-6 h-6 text-blue-600" />Faktura-skabelon</h1>
                    <p className="text-sm text-slate-500">{ALL_FIELD_IDS.length} datafelter + {customTexts.length} egne tekster — {enabledCount} aktive på fakturaen.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="template-enabled-toggle">
                        <span className="text-xs font-medium text-slate-700">Brug template</span>
                        <input type="checkbox" checked={!!template?.enabled} onChange={handleToggleEnabled} className="w-9 h-5 appearance-none bg-slate-200 rounded-full relative checked:bg-emerald-500 cursor-pointer transition before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition checked:before:translate-x-4" />
                    </label>
                    {/* Preset dropdown */}
                    <div className="relative">
                        <Button onClick={() => setPresetMenuOpen((o) => !o)} disabled={applyingPreset} variant="outline" className="text-xs h-9" data-testid="template-presets-btn">
                            {applyingPreset ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" />}Skabeloner<ChevronDown className="w-3 h-3 ml-1.5" />
                        </Button>
                        {presetMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-30" onClick={() => setPresetMenuOpen(false)} />
                                <div className="absolute right-0 top-11 z-40 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 p-2" data-testid="template-presets-menu">
                                    <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Vælg en startskabelon</p>
                                    {presets.length === 0 && <p className="text-xs text-slate-500 px-3 py-2">Indlæser…</p>}
                                    {presets.map((p) => {
                                        const Icon = PRESET_ICONS[p.icon] || FileText;
                                        return (
                                            <button key={p.id} onClick={() => handleApplyPreset(p.id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 flex items-start gap-3" data-testid={`preset-${p.id}`}>
                                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-violet-100 flex items-center justify-center shrink-0">
                                                    <Icon className="w-4 h-4 text-blue-700" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-semibold text-slate-900">{p.label}</p>
                                                    <p className="text-[11px] text-slate-500 leading-snug">{p.description}</p>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">{p.field_count} felter aktive</p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    <Button onClick={handlePreviewPdf} disabled={previewing} variant="outline" className="text-xs h-9" data-testid="template-preview-pdf">
                        {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}Preview PDF
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="template-save" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Gem template
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Canvas */}
                <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4" data-testid="template-canvas-wrap">
                    {!hasBackground ? (
                        <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center" data-testid="template-upload-area">
                            <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-sm text-slate-600 mb-2">Upload baggrundsbillede (PNG eller JPG, max 5 MB)</p>
                            <p className="text-xs text-slate-400 mb-4">Anbefalet: A4 portræt (ca. 1240×1754 px @ 150 DPI)</p>
                            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }} className="hidden" data-testid="template-file-input" />
                            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="template-upload-btn">
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}Vælg fil
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <p className="text-xs text-slate-500">Klik felt for at redigere · Træk for at flytte · Træk hjørne for at ændre størrelse</p>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setSnapEnabled(!snapEnabled)} title="Snap til grid (5px)" className={`text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border ${snapEnabled ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-600"}`} data-testid="template-snap-toggle"><Grid3X3 className="w-3 h-3" />{snapEnabled ? "Snap on" : "Snap off"}</button>
                                    <button onClick={() => replaceInputRef.current?.click()} disabled={uploading} className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="template-replace-bg">
                                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}Skift baggrund
                                    </button>
                                    <input ref={replaceInputRef} type="file" accept="image/png,image/jpeg" onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }} className="hidden" />
                                    <button onClick={handleRemoveBackground} className="text-xs px-2 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5" data-testid="template-remove-bg"><Trash2 className="w-3 h-3" />Slet</button>
                                </div>
                            </div>
                            <div className="overflow-auto bg-slate-100 rounded-lg p-4">
                                <div
                                    ref={canvasRef}
                                    className="relative mx-auto shadow-lg bg-white"
                                    style={{ width: A4_W * scale, height: A4_H * scale, backgroundImage: bgUrl ? `url("${bgUrl}")` : undefined, backgroundSize: "100% 100%", backgroundRepeat: "no-repeat", backgroundColor: bgUrl ? undefined : "#ffffff" }}
                                    onClick={() => setSelectedItem({ kind: "field", id: null })}
                                    data-testid="template-canvas"
                                >
                                    {/* Regular fields */}
                                    {Object.entries(fields).map(([fid, f]) => {
                                        if (!f || !f.enabled) return null;
                                        const x = (f.x_pct / 100) * A4_W * scale, y = (f.y_pct / 100) * A4_H * scale, w = (f.w_pct / 100) * A4_W * scale, h = (f.h_pct / 100) * A4_H * scale;
                                        const isSel = selectedItem.kind === "field" && selectedItem.id === fid;
                                        const z = Number(f.z_index) || 1;
                                        return (
                                            <Rnd key={fid} position={{ x, y }} size={{ width: w, height: h }} style={{ zIndex: z + (isSel ? 100 : 0) }}
                                                onDragStop={(_, d) => { const nx = snap(d.x, snapEnabled), ny = snap(d.y, snapEnabled); updateField(fid, { x_pct: (nx / scale / A4_W) * 100, y_pct: (ny / scale / A4_H) * 100 }); }}
                                                onResizeStop={(_, __, ref, ___, pos) => { const nw = snap(parseFloat(ref.style.width), snapEnabled), nh = snap(parseFloat(ref.style.height), snapEnabled); updateField(fid, { x_pct: (snap(pos.x, snapEnabled) / scale / A4_W) * 100, y_pct: (snap(pos.y, snapEnabled) / scale / A4_H) * 100, w_pct: (nw / scale / A4_W) * 100, h_pct: (nh / scale / A4_H) * 100 }); }}
                                                onMouseDown={(e) => { e.stopPropagation(); setSelectedItem({ kind: "field", id: fid }); }}
                                                bounds="parent" data-testid={`template-field-${fid}`}>
                                                <div className={`w-full h-full rounded-sm flex items-start justify-start px-1 leading-tight overflow-hidden ${isSel ? "border-2 border-blue-500 bg-blue-100/60 shadow-md" : "border border-dashed border-slate-400 bg-slate-100/40 hover:border-blue-400 hover:bg-blue-50/40"}`}
                                                    style={{ fontSize: `${Math.max(8, (f.font_size || 10) * scale * 0.7)}px`, color: f.color, fontWeight: f.font_weight, textAlign: f.align, justifyContent: f.align === "right" ? "flex-end" : f.align === "center" ? "center" : "flex-start" }}>
                                                    <span className="truncate font-mono opacity-80">{FIELD_LABELS[fid]}</span>
                                                </div>
                                            </Rnd>
                                        );
                                    })}
                                    {/* Custom text fields */}
                                    {customTexts.map((c) => {
                                        if (!c.enabled) return null;
                                        const x = (c.x_pct / 100) * A4_W * scale, y = (c.y_pct / 100) * A4_H * scale, w = (c.w_pct / 100) * A4_W * scale, h = (c.h_pct / 100) * A4_H * scale;
                                        const isSel = selectedItem.kind === "custom" && selectedItem.id === c.id;
                                        const z = Number(c.z_index) || 2;
                                        return (
                                            <Rnd key={c.id} position={{ x, y }} size={{ width: w, height: h }} style={{ zIndex: z + (isSel ? 100 : 0) }}
                                                onDragStop={(_, d) => { const nx = snap(d.x, snapEnabled), ny = snap(d.y, snapEnabled); updateCustomText(c.id, { x_pct: (nx / scale / A4_W) * 100, y_pct: (ny / scale / A4_H) * 100 }); }}
                                                onResizeStop={(_, __, ref, ___, pos) => { const nw = snap(parseFloat(ref.style.width), snapEnabled), nh = snap(parseFloat(ref.style.height), snapEnabled); updateCustomText(c.id, { x_pct: (snap(pos.x, snapEnabled) / scale / A4_W) * 100, y_pct: (snap(pos.y, snapEnabled) / scale / A4_H) * 100, w_pct: (nw / scale / A4_W) * 100, h_pct: (nh / scale / A4_H) * 100 }); }}
                                                onMouseDown={(e) => { e.stopPropagation(); setSelectedItem({ kind: "custom", id: c.id }); }}
                                                bounds="parent" data-testid={`template-custom-${c.id}`}>
                                                <div className={`w-full h-full rounded-sm flex items-start justify-start px-1 leading-tight overflow-hidden whitespace-pre-wrap break-words ${isSel ? "border-2 border-emerald-500 bg-emerald-50/60 shadow-md" : "border border-dashed border-emerald-300 bg-emerald-50/30 hover:border-emerald-400 hover:bg-emerald-50/50"}`}
                                                    style={{ fontSize: `${Math.max(8, (c.font_size || 10) * scale * 0.7)}px`, color: c.color, fontWeight: c.font_weight, textAlign: c.align, justifyContent: c.align === "right" ? "flex-end" : c.align === "center" ? "center" : "flex-start" }}>
                                                    <span className="opacity-90">{c.text || "(tom)"}</span>
                                                </div>
                                            </Rnd>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Right panel */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-3 max-h-[520px] overflow-y-auto" data-testid="template-fields-list">
                        <div className="flex items-center justify-between mb-2 px-1">
                            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Datafelter ({enabledCount} aktive)</h3>
                            <button onClick={handleAddCustomText} className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold" data-testid="custom-text-add">
                                <Plus className="w-3 h-3" />Tekstfelt
                            </button>
                        </div>
                        {/* Custom texts group (always first) */}
                        <div className="mb-1" data-testid="field-group-custom">
                            <button onClick={() => setOpenGroups((s) => ({ ...s, custom: !s.custom }))} className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-slate-50 text-left" data-testid="field-group-toggle-custom">
                                {openGroups.custom ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                                <Type className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="flex-1 text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Egne tekstfelter</span>
                                <span className="text-[10px] text-slate-400">{customTexts.length}</span>
                            </button>
                            {openGroups.custom && (
                                <div className="ml-2 pl-2 border-l border-emerald-100 mt-0.5 space-y-0.5">
                                    {customTexts.length === 0 && <p className="text-[10px] text-slate-400 px-2 py-1.5 italic">Ingen — klik &quot;+ Tekstfelt&quot;</p>}
                                    {customTexts.map((c) => {
                                        const isSel = selectedItem.kind === "custom" && selectedItem.id === c.id;
                                        return (
                                            <div key={c.id} className={`flex items-center gap-2 px-2 py-1 rounded-md ${isSel ? "bg-emerald-50" : "hover:bg-slate-50"}`} data-testid={`custom-row-${c.id}`}>
                                                <input type="checkbox" checked={!!c.enabled} onChange={(e) => updateCustomText(c.id, { enabled: e.target.checked })} className="w-3.5 h-3.5 accent-emerald-600" data-testid={`custom-toggle-${c.id}`} />
                                                <button onClick={() => setSelectedItem({ kind: "custom", id: c.id })} className={`flex-1 text-left text-[11px] truncate ${isSel ? "font-semibold text-emerald-700" : "text-slate-700"}`}>{(c.text || "(tom)").slice(0, 28)}</button>
                                                <button onClick={() => handleDeleteCustomText(c.id)} className="text-red-400 hover:text-red-600" title="Slet" data-testid={`custom-delete-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {/* Regular field groups */}
                        {FIELD_GROUPS.map((group) => {
                            const Icon = group.icon;
                            const isOpen = openGroups[group.id];
                            const groupIds = Object.keys(group.fields);
                            const enabledInGroup = groupIds.reduce((n, fid) => n + (fields[fid]?.enabled ? 1 : 0), 0);
                            return (
                                <div key={group.id} className="mb-1" data-testid={`field-group-${group.id}`}>
                                    <button onClick={() => setOpenGroups((s) => ({ ...s, [group.id]: !s[group.id] }))} className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-slate-50 text-left" data-testid={`field-group-toggle-${group.id}`}>
                                        {isOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                                        <Icon className="w-3.5 h-3.5 text-slate-500" />
                                        <span className="flex-1 text-[11px] font-semibold text-slate-700 uppercase tracking-wider">{group.label}</span>
                                        <span className="text-[10px] text-slate-400">{enabledInGroup}/{groupIds.length}</span>
                                    </button>
                                    {isOpen && (
                                        <div className="ml-2 pl-2 border-l border-slate-100 mt-0.5 space-y-0.5">
                                            {Object.entries(group.fields).map(([fid, label]) => {
                                                const f = fields[fid] || {};
                                                const isSel = selectedItem.kind === "field" && selectedItem.id === fid;
                                                return (
                                                    <div key={fid} className={`flex items-center gap-2 px-2 py-1 rounded-md ${isSel ? "bg-blue-50" : "hover:bg-slate-50"}`} data-testid={`field-row-${fid}`}>
                                                        <input type="checkbox" checked={!!f.enabled} onChange={(e) => updateField(fid, { enabled: e.target.checked })} data-testid={`field-toggle-${fid}`} className="w-3.5 h-3.5 accent-blue-600" />
                                                        <button onClick={() => setSelectedItem({ kind: "field", id: fid })} className={`flex-1 text-left text-[11px] truncate ${isSel ? "font-semibold text-blue-700" : "text-slate-700"}`}>{label}</button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Properties panel */}
                    {sel && (
                        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4" data-testid="template-field-props">
                            <div className="flex items-center justify-between mb-3 gap-2">
                                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider truncate flex-1">{selLabel}</h3>
                                {isCustomSel ? (
                                    <button onClick={() => handleDeleteCustomText(selectedItem.id)} className="text-red-500 hover:text-red-700" title="Slet tekstfelt"><Trash2 className="w-3.5 h-3.5" /></button>
                                ) : (
                                    <button onClick={() => handleResetField(selectedItem.id)} title="Nulstil" className="text-slate-400 hover:text-slate-700"><RotateCcw className="w-3.5 h-3.5" /></button>
                                )}
                            </div>
                            <div className="space-y-2 text-xs">
                                {isCustomSel && (
                                    <div>
                                        <Label className="text-[10px] text-slate-500">Tekst (max 500 tegn)</Label>
                                        <Textarea value={sel.text || ""} onChange={(e) => setSel({ text: e.target.value.slice(0, 500) })} rows={3} className="text-xs resize-none" placeholder="Skriv din egen tekst her..." data-testid="custom-text-content" />
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label className="text-[10px] text-slate-500">X (%)</Label><Input type="number" min="0" max="100" step="0.5" value={Number(sel.x_pct || 0).toFixed(1)} onChange={(e) => setSel({ x_pct: Number(e.target.value) })} className="h-8 text-xs" /></div>
                                    <div><Label className="text-[10px] text-slate-500">Y (%)</Label><Input type="number" min="0" max="100" step="0.5" value={Number(sel.y_pct || 0).toFixed(1)} onChange={(e) => setSel({ y_pct: Number(e.target.value) })} className="h-8 text-xs" /></div>
                                    <div><Label className="text-[10px] text-slate-500">Bredde (%)</Label><Input type="number" min="1" max="100" step="0.5" value={Number(sel.w_pct || 0).toFixed(1)} onChange={(e) => setSel({ w_pct: Number(e.target.value) })} className="h-8 text-xs" /></div>
                                    <div><Label className="text-[10px] text-slate-500">Højde (%)</Label><Input type="number" min="1" max="100" step="0.5" value={Number(sel.h_pct || 0).toFixed(1)} onChange={(e) => setSel({ h_pct: Number(e.target.value) })} className="h-8 text-xs" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label className="text-[10px] text-slate-500">Skriftstr. (pt)</Label><Input type="number" min="6" max="48" step="0.5" value={sel.font_size || 10} onChange={(e) => setSel({ font_size: Number(e.target.value) })} className="h-8 text-xs" data-testid="field-prop-font-size" /></div>
                                    <div><Label className="text-[10px] text-slate-500">Z-index (0-10)</Label><Input type="number" min="0" max="10" step="1" value={sel.z_index ?? (isCustomSel ? 2 : 1)} onChange={(e) => setSel({ z_index: Math.max(0, Math.min(10, parseInt(e.target.value, 10) || 0)) })} className="h-8 text-xs" data-testid="field-prop-z-index" /></div>
                                </div>
                                <div>
                                    <Label className="text-[10px] text-slate-500">Vægt</Label>
                                    <div className="grid grid-cols-2 gap-1">
                                        {["normal", "bold"].map((w) => (
                                            <button key={w} onClick={() => setSel({ font_weight: w })} className={`text-[10px] px-2 py-1 rounded-md border ${sel.font_weight === w ? "bg-blue-50 border-blue-200 text-blue-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>{w === "bold" ? "Fed" : "Normal"}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[10px] text-slate-500">Justering</Label>
                                    <div className="grid grid-cols-3 gap-1">
                                        {ALIGN_OPTIONS.map((a) => (
                                            <button key={a.id} onClick={() => setSel({ align: a.id })} className={`text-xs px-2 py-1 rounded-md border ${sel.align === a.id ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-600"}`}>{a.label}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[10px] text-slate-500">Farve</Label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={sel.color || "#0f172a"} onChange={(e) => setSel({ color: e.target.value })} className="w-9 h-8 rounded border border-slate-200 cursor-pointer" />
                                        <Input value={sel.color || "#0f172a"} onChange={(e) => setSel({ color: e.target.value })} className="h-8 text-xs flex-1 font-mono" />
                                    </div>
                                </div>
                                {!isCustomSel && (
                                    <div>
                                        <Label className="text-[10px] text-slate-500">Prefix (foran tekst)</Label>
                                        <Input value={sel.prefix ?? ""} onChange={(e) => setSel({ prefix: e.target.value })} className="h-8 text-xs" placeholder="fx 'CVR: '" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-blue-900 leading-relaxed">Tip: Klik <b>Skabeloner</b> for at starte fra et færdigt layout. Tilføj <b>egne tekstfelter</b> (grøn ramme) til reklamer, garantier, hilsner. Tomme felter tegnes ikke på PDF&apos;en.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
